import React from "react";
import { Trophy, CalendarBlank, MapPin, Basketball } from "@phosphor-icons/react";

const COLS = [
  { key: "pts", label: "PTS" },
  { key: "reb", label: "REB" },
  { key: "ast", label: "AST" },
  { key: "stl", label: "STL" },
  { key: "blk", label: "BLK" },
  { key: "pf", label: "PF" },
];

function StatTable({ team, entries, totals, pogId }) {
  return (
    <div className="mb-6" data-testid={`boxscore-team-${team?.id || "x"}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 flex items-center justify-center text-black font-display text-sm overflow-hidden shrink-0" style={{ background: team?.primary_color || "#FF5722" }}>
          {team?.logo_url ? <img src={team.logo_url} alt="" className="w-full h-full object-cover" /> : (team?.name?.charAt(0) || "?")}
        </span>
        <span className="font-display text-xl uppercase">{team?.name || "TBD"}</span>
      </div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-[#1A1A1A] text-white/60 uppercase text-[11px] tracking-wide">
              <th className="text-left px-3 py-2 sticky left-0 bg-[#1A1A1A]">Player</th>
              {COLS.map((c) => <th key={c.key} className="px-2 py-2 text-center">{c.label}</th>)}
              <th className="px-2 py-2 text-center">3PT</th>
              <th className="px-2 py-2 text-center">FT</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={COLS.length + 3} className="px-3 py-4 text-center text-white/30">No player stats recorded.</td></tr>
            )}
            {entries.map((s) => {
              const isPog = s.player_id === pogId;
              return (
                <tr key={s.player_id} className={`border-t border-white/5 ${isPog ? "bg-[#FF5722]/10" : ""}`}>
                  <td className="px-3 py-2 sticky left-0 bg-[#121212] whitespace-nowrap">
                    <span className="text-[#FF5722] font-display mr-1.5 tabular-nums">{s.number != null ? `#${s.number}` : ""}</span>
                    <span className="uppercase font-semibold">{s.name}</span>
                    {isPog && <Trophy size={13} weight="fill" className="inline ml-1.5 text-[#FF5722]" />}
                  </td>
                  {COLS.map((c) => <td key={c.key} className="px-2 py-2 text-center tabular-nums">{s[c.key] || 0}</td>)}
                  <td className="px-2 py-2 text-center tabular-nums text-white/70">{s.tpm || 0}/{s.tpa || 0}</td>
                  <td className="px-2 py-2 text-center tabular-nums text-white/70">{s.ftm || 0}/{s.fta || 0}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/15 bg-[#161616] font-bold">
              <td className="px-3 py-2 sticky left-0 bg-[#161616] uppercase text-white/70">Totals</td>
              {COLS.map((c) => <td key={c.key} className="px-2 py-2 text-center tabular-nums">{totals[c.key] || 0}</td>)}
              <td className="px-2 py-2 text-center tabular-nums text-white/70">{totals.tpm || 0}/{totals.tpa || 0}</td>
              <td className="px-2 py-2 text-center tabular-nums text-white/70">{totals.ftm || 0}/{totals.fta || 0}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export function BoxScoreView({ box }) {
  const homeWon = box.home_score >= box.away_score;
  return (
    <div>
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-3 sm:gap-6 mb-3">
          <div className="flex-1 text-right font-display text-xl sm:text-2xl uppercase">{box.home_team.name}</div>
          <div className="font-display text-4xl sm:text-5xl tabular-nums shrink-0">
            <span className={homeWon ? "text-[#FF5722]" : "text-white/60"}>{box.home_score}</span>
            <span className="text-white/30 mx-2">-</span>
            <span className={!homeWon ? "text-[#FF5722]" : "text-white/60"}>{box.away_score}</span>
          </div>
          <div className="flex-1 text-left font-display text-xl sm:text-2xl uppercase">{box.away_team.name}</div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/50">
          <span className="flex items-center gap-1"><CalendarBlank size={14} /> {box.date ? new Date(box.date).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
          {box.week != null && <span className="uppercase tracking-wide">Week {box.week}</span>}
          {box.division && <span className="uppercase tracking-wide text-[#FF5722]">{box.division}</span>}
          {box.court && <span className="flex items-center gap-1"><Basketball size={14} weight="fill" /> {box.court}</span>}
          {box.venue && <span className="flex items-center gap-1"><MapPin size={14} /> {box.venue}</span>}
        </div>
        {box.player_of_game_name && (
          <div className="mt-3 inline-flex items-center gap-2 bg-[#FF5722]/15 border border-[#FF5722]/40 px-4 py-1.5" data-testid="boxscore-pog">
            <Trophy size={16} weight="fill" className="text-[#FF5722]" />
            <span className="text-sm uppercase tracking-wide">Player of the Game: <span className="font-bold text-[#FF5722]">{box.player_of_game_name}</span></span>
          </div>
        )}
      </div>

      <StatTable team={box.home_team} entries={box.home_stats} totals={box.home_totals} pogId={box.player_of_game} />
      <StatTable team={box.away_team} entries={box.away_stats} totals={box.away_totals} pogId={box.player_of_game} />
    </div>
  );
}
